import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ADMIN_SET_CPM_CAMPAIGN_VIDEO_EXCLUDED } from '@/queries/queries';

export interface RemovableCampaignVideo {
  /** Source youtube_download documentId. */
  youtube_download_id?: string | null;
  name: string;
}

interface RemoveCampaignVideoDialogProps {
  campaignId: string;
  /** The video being removed; the dialog is open while it is set. */
  video: RemovableCampaignVideo | null;
  onOpenChange: (open: boolean) => void;
  onRemoved: () => void;
}

/**
 * Admin confirmation that removes one video from a campaign. The removal wins
 * over the campaign's listing and attached channels, so a video a shared
 * channel brings in can be dropped without touching the rest of the channel.
 */
export function RemoveCampaignVideoDialog({
  campaignId, video, onOpenChange, onRemoved,
}: RemoveCampaignVideoDialogProps) {
  const { t } = useTranslation();
  const [setExcluded, { loading }] = useMutation(ADMIN_SET_CPM_CAMPAIGN_VIDEO_EXCLUDED);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const videoId = video?.youtube_download_id;

  const confirm = async () => {
    if (!videoId) return;
    try {
      await setExcluded({ variables: { campaignId, videoId, excluded: true } });
      onOpenChange(false);
      onRemoved();
    } catch {
      onOpenChange(false);
      setErrorMessage(t('campaign.removeVideo.error'));
    }
  };

  return (
    <>
      <Dialog open={!!video} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('campaign.removeVideo.title')}</DialogTitle>
            <DialogDescription>{t('campaign.removeVideo.description')}</DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium">{video?.name}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {t('campaign.removeVideo.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirm} disabled={loading || !videoId}>
              {t('campaign.removeVideo.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('campaign.removeVideo.errorTitle')}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
